/**
 * Delegate the event listening.
 *
 * @example
 *   document.addEventListener(
 *     'click',
 *     new DelegatedEventListener('button[type=button]', (event, button) => console.log(event, button))
 *   );
 *   document.addEventListener(
 *     'click',
 *     new DelegatedEventListener(
 *       (target) => target.closest('button[type=button]'),
 *       (event, button) => console.log(event, button)
 *     )
 *   );
 *
 * @implements {EventListenerObject}
 * @see https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/Scripting/Event_bubbling#event_delegation
 */

export default class {
  #listener;
  #selector;

  /**
   * @param {string|function(EventTarget):(Node|boolean|null|undefined)} selector
   * @param {DelegatedEventListenerOrDelegatedEventListenerObject} listener
   */
  constructor(selector, listener) {
    this.#listener = (event, delegatedTarget) =>
      typeof listener === 'function'
        ? listener.call(null, event, delegatedTarget)
        : listener?.handleEvent?.(event, delegatedTarget);
    this.#selector =
      typeof selector === 'function'
        ? selector
        : ((selector, target) => target.closest(selector)).bind(null, String(selector));
  }

  /**
   * @throws {TypeError}
   * @inheritDoc
   */
  handleEvent(event) {
    const { target } = event;
    const result = this.#selector.call(null, target); // TODO try?
    // Don't delegate the event when selector returns false, null or undefined
    if (result === false || result == null) return;

    const delegatedTarget = result instanceof Node ? result : target;

    if (
      delegatedTarget !== target &&
      target.compareDocumentPosition(delegatedTarget) !== Node.DOCUMENT_POSITION_CONTAINS
    ) {
      throw new TypeError('Delegated target is not descendant of target');
    }

    this.#listener(event, delegatedTarget);
  }
}

/** @typedef {import('./typedefs.js').EventListenerObject} EventListenerObject */
/** @typedef {import('./typedefs.js').EventListenerOrEventListenerObject} EventListenerOrEventListenerObject */

/** @typedef {EventListenerOrEventListenerObject | DelegatedEventListener | DelegatedEventListenerObject} DelegatedEventListenerOrDelegatedEventListenerObject */

/**
 * @callback DelegatedEventListener
 * @param {Event} event
 * @param {Node} target
 */

/**
 * @typedef {object} DelegatedEventListenerObject
 * @property {function(Event, Node): void} handleEvent
 */
